"use client";

import { useEffect, useState } from "react";
import Preloader from "./preloader";
import { SidebarInset, SidebarProvider } from "./ui/sidebar";
import { AppSidebar } from "./app-sidebar";
import { useUserStore } from "@/stores/user";
import { useStoreHydration } from "@/hooks/useStoreHydration";
import { useOrganizationStore } from "@/stores/organization";
import OrganizationCreateDialog from "./organization-create-dialog";
import { login, logout } from "@/app/actions";
import {
  isTransientApiError,
  isUnauthorizedApiError,
} from "@/services/api-client";
import { deleteCookie, getCookie } from "@/lib/cookies";
import InviteAcceptDialog from "./invite-accept-dialog";
import {
  getOrganizationJoinRequestStatus,
  getPendingOrganizationJoinRequest,
  updateOrganizationMemberByInivitationDetails,
} from "@/services/member.client";
import { DEFAULT_ORG_AVATAR } from "@/lib/constants";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { Button } from "./ui/button";
import { LogIn, RefreshCw } from "lucide-react";

type AppBootstrapProps = {
  children: React.ReactNode;
};

function getOrganizationBootstrapError(error: unknown): string | null {
  if (isUnauthorizedApiError(error)) {
    return "We couldn't verify your organization access.";
  }

  if (isTransientApiError(error)) {
    return "We couldn't load your organization.";
  }

  return null;
}

export default function AppBootstrap({ children }: AppBootstrapProps) {
  const isHydrated = useStoreHydration();
  const { fetchUser, updateUser, user } = useUserStore();
  const {
    fetchOrganization,
    organization,
    pendingJoinRequest,
    setMembers,
    setOrganization,
    setPendingJoinRequest,
  } = useOrganizationStore();
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [isCreateOrganization, setIsCreateOrganization] = useState(false);
  const [invitation, setInvitation] = useState<{
    orgId: string;
    userId: string;
    token: string;
  } | null>(null);
  const organizationId = organization?.id;

  useEffect(() => {
    (async () => {
      if (!isHydrated) return;

      const inviteParams = getCookie("inviteParams");

      if (inviteParams) {
        const params = new URLSearchParams(inviteParams);
        const orgId = params.get("orgId");
        const userId = params.get("userId");
        const token = params.get("token");

        if (orgId && userId && token) {
          setInvitation({ orgId, userId, token });
        }
      }

      try {
        setBootstrapError(null);
        await fetchUser();
      } catch (error: any) {
        if (isTransientApiError(error)) {
          console.warn("User bootstrap failed after transient API failure.");
          showApiErrorToast("Failed to load user", error);
          setBootstrapError("We couldn't verify your account.");
          setIsBootstrapped(false);
          return;
        }

        if (!inviteParams) {
          console.warn("Failed to fetch user", error.message);
          // On error, forcibly redirect to login page if there's no logged in user
          setIsBootstrapped(false);
          try {
            await logout();
          } catch (logoutError: any) {
            // If logout also fails (e.g., auth server down), redirect via login instead
            console.warn(
              "Logout failed, trying login redirect:",
              logoutError.message,
            );
            try {
              await login();
            } catch (loginError: any) {
              showApiErrorToast("Auth redirect failed", loginError);
              setBootstrapError("We couldn't verify your account.");
              setIsBootstrapped(false);
            }
          }
        } else {
          console.warn(
            "User bootstrap failed while handling invitation",
            error.message,
          );
          setIsBootstrapped(true);
        }
      }
    })();
  }, [fetchUser, fetchOrganization, isHydrated]);

  useEffect(() => {
    (async () => {
      if (!user) return;

      setIsCreateOrganization(false);

      const hydratePendingJoinRequest = async () => {
        try {
          const { request, organization: requestedOrganization } =
            await getPendingOrganizationJoinRequest();

          if (request?.status === "requested" && requestedOrganization) {
            setPendingJoinRequest({
              ...requestedOrganization,
              logo: requestedOrganization.logo || DEFAULT_ORG_AVATAR,
              joinRequestId: request.id,
            });
            setOrganization(null);
            setIsCreateOrganization(false);
            setIsBootstrapped(true);
            return true;
          }
        } catch (error: any) {
          console.warn(
            "Failed to check pending organization request",
            error.message,
          );
          setOrganization(null);
          setIsCreateOrganization(false);
          setIsBootstrapped(true);
          return true;
        }

        return false;
      };

      const resolvePersistedPendingJoinRequest = async () => {
        if (!pendingJoinRequest) return false;

        try {
          const { request, organization: requestedOrganization } =
            await getOrganizationJoinRequestStatus(pendingJoinRequest.id);

          if (request?.status === "requested" && requestedOrganization) {
            setPendingJoinRequest({
              ...requestedOrganization,
              logo: requestedOrganization.logo || DEFAULT_ORG_AVATAR,
              joinRequestId: request.id,
            });
            setOrganization(null);
            setIsCreateOrganization(false);
            setIsBootstrapped(true);
            return true;
          }

          setPendingJoinRequest(null);

          if (request?.status === "active") {
            await fetchUser(true);
            await fetchOrganization(pendingJoinRequest.id, true, true);
            setIsCreateOrganization(false);
            setIsBootstrapped(true);
            return true;
          }
        } catch (error: any) {
          console.warn(
            "Failed to refresh pending organization request",
            error.message,
          );
          setPendingJoinRequest(null);
        }

        return false;
      };

      if (pendingJoinRequest) {
        if (await resolvePersistedPendingJoinRequest()) return;
      }

      if (
        user &&
        user.organizations.length === 0 &&
        !pendingJoinRequest &&
        !organizationId
      ) {
        if (await hydratePendingJoinRequest()) return;

        setOrganization(null);
        setIsCreateOrganization(true);
        setIsBootstrapped(true);
        return;
      }

      if (user && user.organizations.length === 0 && organizationId) {
        setIsCreateOrganization(false);
        setIsBootstrapped(true);
        return;
      }

      const lastSelectedOrgId = getCookie("lastSelectedOrganization");
      // Validate that the cookie's org ID exists in user's current org list
      // to prevent 403 errors from stale cookies
      const validLastSelectedOrgId =
        lastSelectedOrgId && user.organizations.includes(lastSelectedOrgId)
          ? lastSelectedOrgId
          : null;
      const orgId = validLastSelectedOrgId || user.organizations[0];

      if (orgId && user.organizations.length > 0) {
        if (organizationId === orgId) {
          setPendingJoinRequest(null);
          setIsCreateOrganization(false);
          setIsBootstrapped(true);
          return;
        }

        // Try the selected org first, then fall back through all other orgs
        const orgsToTry = [
          orgId,
          ...user.organizations.filter((id) => id !== orgId),
        ];
        const staleOrganizationIds = new Set<string>();

        for (const tryOrgId of orgsToTry) {
          try {
            await fetchOrganization(tryOrgId, false, true);
            const fetchedOrganization =
              useOrganizationStore.getState().organization;
            const hasActiveMembership = useOrganizationStore
              .getState()
              .members.some(
                (member) =>
                  member.organizationId === tryOrgId &&
                  member.userId === user.id &&
                  member.status === "active",
              );
            const hasOwnerAccess = fetchedOrganization?.ownerId === user.id;

            if (!hasActiveMembership && !hasOwnerAccess) {
              staleOrganizationIds.add(tryOrgId);
              if (lastSelectedOrgId === tryOrgId) {
                deleteCookie("lastSelectedOrganization", "/");
              }
              setOrganization(null);
              setMembers([]);
              continue;
            }

            if (staleOrganizationIds.size > 0) {
              await updateUser({
                name: user.name,
                email: user.email,
                avatar: user.avatar,
                organizations: user.organizations.filter(
                  (id) => !staleOrganizationIds.has(id),
                ),
              });
            }

            setPendingJoinRequest(null);
            setIsBootstrapped(true);
            return;
          } catch (error: any) {
            const blockingError = getOrganizationBootstrapError(error);

            if (blockingError) {
              console.warn(
                `Organization bootstrap failed for '${tryOrgId}'. ${error.message}`,
              );
              showApiErrorToast("Failed to load organization", error);
              setBootstrapError(blockingError);
              setIsCreateOrganization(false);
              setIsBootstrapped(false);
              return;
            }

            console.warn(
              `Failed to fetch organization '${tryOrgId}'. ${error.message}`,
            );
          }
        }

        if (staleOrganizationIds.size > 0) {
          await updateUser({
            name: user.name,
            email: user.email,
            avatar: user.avatar,
            organizations: user.organizations.filter(
              (id) => !staleOrganizationIds.has(id),
            ),
          });
        }
      }

      if (await hydratePendingJoinRequest()) return;

      setIsCreateOrganization(true);
      setIsBootstrapped(true);
    })();
  }, [
    fetchOrganization,
    fetchUser,
    organizationId,
    pendingJoinRequest,
    setMembers,
    setOrganization,
    setPendingJoinRequest,
    updateUser,
    user,
  ]);

  if (bootstrapError) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-background p-6">
        <div className="flex w-full max-w-sm flex-col items-center gap-5 text-center">
          <div className="flex flex-col gap-2">
            <span className="font-headline text-xs font-semibold uppercase text-brand-accent">
              Libum Portal
            </span>
            <h1 className="text-xl font-semibold text-foreground">
              {bootstrapError}
            </h1>
            <p className="text-sm text-muted-foreground">
              Your account and organization access are required before the
              portal can load.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => window.location.reload()}>
              <RefreshCw />
              Retry
            </Button>
            <Button
              variant="secondary"
              onClick={async () => {
                try {
                  await logout();
                } catch (error) {
                  showApiErrorToast("Auth redirect failed", error);
                }
              }}
            >
              <LogIn />
              Sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!isBootstrapped) return <Preloader />;

  return (
    <>
      <SidebarProvider>
        <AppSidebar />
        <SidebarInset>
          <div className="lb-scrollbar-overlay flex h-svh max-h-svh w-full min-w-0 flex-1 flex-col overflow-x-hidden md:h-[calc(100svh-theme(spacing.4))] md:max-h-[calc(100svh-theme(spacing.4))] md:overflow-hidden md:rounded-xl">
            {children}
          </div>
        </SidebarInset>
      </SidebarProvider>
      {/* Prompts when user has no organizations */}
      <OrganizationCreateDialog
        controlledOpen={isCreateOrganization && !invitation && !!user}
        isForced={true}
        onSuccess={() => {
          setIsCreateOrganization(false);
        }}
        onJoinRequestSuccess={() => {
          setIsCreateOrganization(false);
        }}
      />
      {/* Prompts when an invitation has been opened */}
      <InviteAcceptDialog
        controlledOpen={!!invitation}
        user={user}
        invitation={invitation!}
        onAccept={async (updateData) => {
          deleteCookie("inviteParams", "/");

          try {
            const { userId, organizationId } =
              await updateOrganizationMemberByInivitationDetails(updateData);

            if (user && user.id === userId) {
              await fetchOrganization(organizationId, true);
              setInvitation(null);
            } else {
              // @TODO: It would be best if we could just log them in here
              setIsBootstrapped(false);
              setInvitation(null);
              logout();
            }
          } catch (error: any) {
            console.warn("Failed to accept invitation", error.message);
            // On error, forcibly redirect to login page if there's no logged in user
            if (user === null) {
              setIsBootstrapped(false);
              logout();
            }
          }
        }}
        onDecline={async (updateData) => {
          deleteCookie("inviteParams", "/");

          // Update if declined, ignore if dismissed
          if (updateData) {
            await updateOrganizationMemberByInivitationDetails(updateData);
          }

          if (user === null) {
            setIsBootstrapped(false);
            await login();
          } else {
            setInvitation(null);
          }
        }}
      />
    </>
  );
}
